import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchUserProfileAcls, fetchUserProfileAclsAndLicenses } from 'common/services/api/UserProfileAcls/UserProfileAcls.thunk';
import { FormattedUserAcl, FormattedUserAclWithLicenseInfo } from 'common/services/api/UserProfileAcls/UserProfileAclsThunkTypeDefs';
import { createMemoizedKeySelector } from 'common/utils/selectorUtils';

interface UserProfileAclsSliceState {
    userAclsByTenantId: Record<string, FormattedUserAcl[]>;
    userProfileAclsWithLicenseByTenantId: Record<string, FormattedUserAclWithLicenseInfo[]>;
    processing: string | false;
    processingUserProfileAclsWithLicense: string | false;
}

type StateWithUserProfileAcls = { userProfileAcls: UserProfileAclsSliceState };

const initialState: UserProfileAclsSliceState = {
    userAclsByTenantId: {},
    userProfileAclsWithLicenseByTenantId: {},
    processing: false,
    processingUserProfileAclsWithLicense: false,
};

const userProfileAclsSlice = createSlice({
    name: 'userProfileAcls',
    initialState,

    reducers: (create) => ({
        clearUserProfileAcls: create.reducer(state => {
            state.userAclsByTenantId = {};
            state.userProfileAclsWithLicenseByTenantId = {};
        })
    }),

    extraReducers: builder => {
        builder.addCase(fetchUserProfileAcls.pending, (state, args) => {
            state.processing = args.meta.arg.tenantId;
            state.userAclsByTenantId[args.meta.arg.tenantId] = [];
        });
        builder.addCase(fetchUserProfileAcls.fulfilled, (state, action: PayloadAction<FormattedUserAcl[]>) => {
            state.processing = false;
            if(action.payload.length) {
                const tenantId = action.payload[0].tenantId;
                state.userAclsByTenantId = {
                    ...state.userAclsByTenantId,
                    [tenantId]: action.payload
                };
            }
        });
        builder.addCase(fetchUserProfileAcls.rejected, (state, args) => {
            state.processing = false;
            state.userAclsByTenantId[args.meta.arg.tenantId] = [];
        });
        builder.addCase(fetchUserProfileAclsAndLicenses.pending, (state, args) => {
            state.processingUserProfileAclsWithLicense = args.meta.arg.currentUserProfileTenant.id;
            state.userProfileAclsWithLicenseByTenantId[args.meta.arg.currentUserProfileTenant.id] = [];
        });
        builder.addCase(fetchUserProfileAclsAndLicenses.fulfilled, (state, action: PayloadAction<FormattedUserAclWithLicenseInfo[]>) => {
            state.processingUserProfileAclsWithLicense = false;
            if(action.payload.length) {
                const tenantId = action.payload[0].tenantId;
                state.userProfileAclsWithLicenseByTenantId = {
                    ...state.userProfileAclsWithLicenseByTenantId,
                    [tenantId]: action.payload
                };
            }
        });
        builder.addCase(fetchUserProfileAclsAndLicenses.rejected, (state, args) => {
            state.processingUserProfileAclsWithLicense = false;
            state.userProfileAclsWithLicenseByTenantId[args.meta.arg.currentUserProfileTenant.id] = [];
        });
    }
});

// eslint-disable-next-line no-restricted-syntax
export default userProfileAclsSlice.reducer;
export const { clearUserProfileAcls } = userProfileAclsSlice.actions;

// Base selectors for byTenantId state
const selectUserAclsByTenantIdState = (state: StateWithUserProfileAcls) => state.userProfileAcls.userAclsByTenantId;
const selectUserProfileAclsWithLicenseByTenantIdState = (state: StateWithUserProfileAcls) => state.userProfileAcls.userProfileAclsWithLicenseByTenantId;

// Memoized parameterized selectors
export const selectUserProfileAclsByTenantId = createMemoizedKeySelector(selectUserAclsByTenantIdState);
export const selectProcessing = (state: StateWithUserProfileAcls) => state.userProfileAcls.processing;
export const selectUserProfileAclsWithLicenseByTenantId = createMemoizedKeySelector(selectUserProfileAclsWithLicenseByTenantIdState);
export const selectProcessingUserProfileAclsWithLicense = (state: StateWithUserProfileAcls) => state.userProfileAcls.processingUserProfileAclsWithLicense;
